import { type CSSResultGroup, html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ContextRoot, provide } from '@lit/context'
import { debounce, emit, mapAssistDomainToFeatureFlagDomain } from '@/navbar/utils'
import { isArrowDown, isArrowUp, isShift, isTab } from '@/navbar/utils/eventKeys'
import { logError, setErrorReporter, setLogLevel, type ErrorReporter, type LogLevel } from '@/utils/errorReporter'
import { DEFAULT_LOGOMARK_SRC } from '@/navbar/constants'
import { $t, setLocale } from '@/navbar/i18n'
import { NAMESPACE_LABELS } from '@/navbar/constants/namespace-labels'
import { FEATURE_FLAG_DOMAINS, ECOID_DOMAINS, ASSIST_DOMAINS } from '@/navbar/constants/domains'
import { subscribe, unsubscribe } from '@/i18n/subscribers'
import fallbackMarketingData from '@/navbar/data/fallbackMarketingData'
import { navbarContext, type NavbarPart } from './context'

// APIs:
import MarketingDataApi from '@/navbar/api/marketingData.api'
import IdentityApi from '@/navbar/api/identity.api'
import AssistApi from '@/navbar/api/assist.api'
import FeatureFlagApi from '@/navbar/api/featureFlag.api'

// Types/local class imports:
import { type EcoSuiteNavbarMobileMenu } from './mobile-menu/eco-suite-navbar-mobile-menu.component'
import { type ResolvedTarget } from './skip-to-content/eco-suite-navbar-skip-to-content.component'
import type { EcoSuiteNavbarAppSwitcher } from './app-switcher/eco-suite-navbar-app-switcher.component'
import type { EcoSuiteNavbarButton } from './button/eco-suite-navbar-button.component'
import type { Region, MenuItem, UserProduct, SkipToContentTarget, EcoAssist, NavbarAppSwitcherMarketingData } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar.scss?inline'

// Child components:
import './app-switcher/eco-suite-navbar-app-switcher.component'
import './tooltip/eco-suite-navbar-tooltip.component'
import './help/eco-suite-navbar-help.component'
import './account-menu/eco-suite-navbar-account-menu.component'
import './button/eco-suite-navbar-button.component'
import './mobile-menu/eco-suite-navbar-mobile-menu.component'
import './skip-to-content/eco-suite-navbar-skip-to-content.component'
import './side-menu-button/eco-suite-navbar-side-menu-button.component'

// Prism imports:
import '../prism/components'
import '../prism/icons'

/**
 * @summary The main navbar component. Handles authentication, app switching, help, account menus, and responsive layout.
 * @tag eco-suite-navbar
 * @slot home-suffix - Content rendered immediately after the logo/home link.
 * @slot search - Search input or control rendered in the navbar toolbar. Be sure to give your search slot content `width: 100%` so it behaves as expected.
 * @slot utilities - Additional utility controls rendered in the right section of the navbar.
 * @slot help-menu - Content rendered inside the help menu trigger area.
 * @slot account-menu - Content rendered inside the account menu trigger area.
 * @csspart side-menu-toggle - The button that toggles the side menu, if {@link hasSideMenu} is true. Useful for controlling visibility of the button via media queries.
 * @cssproperty [--eco-suite-navbar-z-index=1] - Controls the stacking order of the entire navbar.
 * @event eco-mobile-menu-opened - Fired when the mobile menu is opened.
 * @event eco-mobile-menu-closed - Fired when the mobile menu is closed.
 * @event eco-assist-opened - Fired when the Assist panel is opened.
 * @event eco-assist-closed - Fired when the Assist panel is closed.
 * @event eco-assist-minimised - Fired when the Assist panel is minimised.
 * @event eco-search-expanded - Fired when the search bar is expanded (on mobile/tablet).
 * @event eco-search-collapsed - Fired when the search bar is collapsed (on mobile/tablet).
 */
@customElement('eco-suite-navbar')
export class EcoSuiteNavbar extends LitElement {
  /** @internal */ static readonly tagName = 'eco-suite-navbar'
  /** @internal */ static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  /** @internal */ public static readonly DEFAULT_LOCALE = 'en'
  /** @internal */ private resizeObserver?: ResizeObserver
  /** @internal */ private searchCloseWatcher?: CloseWatcher | null
  /** @internal */ private _clickListener?: (e: MouseEvent) => void
  /** @internal */ private _booted = false

  /** @internal */ private readonly _onAssistOpen = () => {
    this.mobileMenu?.close?.()
    emit(this, 'eco-assist-opened')
  }
  /** @internal */ private readonly _onAssistClose = () => emit(this, 'eco-assist-closed')
  /** @internal */ private readonly _onAssistMinimise = () => emit(this, 'eco-assist-minimised')

  /** @internal */
  @queryAssignedElements({ slot: 'search' })
  private readonly searchSlotElements?: HTMLElement[]

  /** @internal */
  @query('.EcoSuiteNavbar__mobileToggle')
  private readonly mobileMenuToggleButton?: EcoSuiteNavbarButton

  /** @internal */
  @query('eco-suite-navbar-mobile-menu')
  private readonly mobileMenu?: EcoSuiteNavbarMobileMenu

  /** @internal */
  @query('.EcoSuiteNavbar__searchBarToggle')
  private readonly searchToggleButton?: HTMLElement

  /** @internal */
  @query('eco-suite-navbar-app-switcher')
  private readonly appSwitcher?: EcoSuiteNavbarAppSwitcher

  /** Callback returning a valid EcoID bearer token (required if not passing the user's info manually). */
  @property({ attribute: false })
  getToken?: () => Promise<string>

  /** Domain for EcoID calls (if {@link getToken} is provided). */
  @property({ attribute: 'eco-id-domain' })
  ecoIdDomain?: string

  /** Domain for Assist calls. */
  @property({ attribute: 'assist-domain' })
  assistDomain?: string

  /**
   * Domain for Feature Flag Service calls. Prefer passing the {@link region} if available.
   * The Feature Flag Service gates App Switcher visibility: the App Switcher is **hidden**
   * unless the service confirms it is enabled for the current organisation/tenant + product.
   * If a domain cannot be resolved from this prop, the App Switcher is hidden.
   */
  @property({ attribute: 'feature-flag-domain' })
  featureFlagDomain?: string

  /**
   * Platform URL for the All Apps link in the App Switcher. EcoID users only (not required if {@link getToken} is provided).
   * `links.homepage` property from the `userdata` endpoint can be used in place of an environment variable.
   * Falls back to a marketing site URL if empty.
   */
  @property({ attribute: 'platform-url' })
  platformUrl?: string

  /** Identifier of the current product. */
  @property({ attribute: 'current-product' })
  currentProduct?: string

  /** User's locale in RFC-5646 format */
  @property()
  locale: string = EcoSuiteNavbar.DEFAULT_LOCALE

  /**
   * User's region. Used to resolve default service domains (EcoID, Assist, Feature Flag Service)
   * when the explicit `*-domain` properties are not provided.
   * We don't want to be overly restrictive here - the Region type is mainly for guidance; we also
   * accept arbitrary strings.
   */
  @property()
  region?: Region | string

  /** Where to link to from the home anchor. */
  @property({ attribute: 'home-href' })
  homeHref = globalThis.location.origin

  /** Callback for when the home anchor is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onHomeClick?: (e: Event) => void

  /** Whether the user is a guest. */
  @property({ type: Boolean, attribute: 'is-guest' })
  isGuest = false

  /** Whether to hide the Account Menu. */
  @property({ type: Boolean, attribute: 'hide-account-menu' })
  hideAccountMenu = false

  /** Items to display in the Account Menu. */
  @property({ type: Array, attribute: 'account-menu-items' })
  accountMenuItems?: MenuItem[]

  /** Callback to execute when the Logout item is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onLogout?: (e: Event) => void

  /** URL to navigate to when the Logout item is clicked */
  @property({ attribute: 'logout-href' })
  logoutHref?: string

  /** Items to display in the Help Menu. */
  @property({ type: Array, attribute: 'help-menu-items' })
  helpMenuItems?: MenuItem[]

  /** Where to link to from the Help button or Help menu item. */
  @property({ attribute: 'help-href' })
  helpHref?: string

  /** Callback to execute when the Help button or Help menu item is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onHelpClick?: (e: Event) => void

  /** Custom logo URL if white-labelling is enabled for the user's organisation (not required if {@link getToken} is provided). */
  @property({ attribute: 'logo-src' })
  logoSrc = DEFAULT_LOGOMARK_SRC

  /** User's full name (not required if {@link getToken} is provided). */
  @property({ attribute: 'user-name' })
  userName?: string

  /** User's list of products from the EcoID userdata endpoint (not required if {@link getToken} is provided). */
  @property({ type: Array, attribute: 'user-products' })
  userProducts: UserProduct[] = []

  /** User's organisation ID (not required if {@link getToken} is provided). */
  @property({ attribute: 'organisation-id' })
  organisationId?: string

  /** User's organisation name (not required if {@link getToken} is provided). */
  @property({ attribute: 'organisation-name' })
  organisationName?: string

  /** User's tenant ID (not required if organisation ID is provided, manually or via {@link getToken}). */
  @property({ attribute: 'tenant-id' })
  tenantId?: string

  /** Skip-to-content targets. When not provided, defaults to the first focusable element in the document. */
  @property({ attribute: false })
  skipToContent?: SkipToContentTarget | SkipToContentTarget[]

  /** Optional error reporter for forwarding errors to an external monitoring service (e.g. Datadog RUM). */
  @property({ attribute: false })
  set errorReporter(reporter: ErrorReporter | undefined) {
    this._errorReporter = reporter
    setErrorReporter(reporter)
  }
  get errorReporter(): ErrorReporter | undefined {
    return this._errorReporter
  }
  /** @internal */
  private _errorReporter?: ErrorReporter

  /** Minimum severity level for log output (default: 'error'). */
  @property({ attribute: 'log-level' })
  set logLevel(level: LogLevel) {
    this._logLevel = level
    setLogLevel(level)
  }
  get logLevel(): LogLevel {
    return this._logLevel
  }
  /** @internal */
  private _logLevel: LogLevel = 'error'

  /** Whether to disable the Assist button, e.g. in Offline mode. */
  @property({ type: Boolean, attribute: 'disable-assist' })
  disableAssist = false

  /**
   * Whether the application has a side menu. If `true`, a toggle button will be displayed.
   * You can further configure the display of this button (e.g. using media queries) via the
   * `side-menu-toggle` CSS part.
   * Note that the navbar does not implement the side menu itself; you will need
   * to implement that separately and toggle its visibility in the callback provided to {@link onSideMenuToggle}.
   * */
  @property({ type: Boolean, attribute: 'has-side-menu' })
  hasSideMenu = false

  /** Whether the side menu is open. */
  @property({ type: Boolean, attribute: 'side-menu-open' })
  sideMenuOpen = false

  /** Callback to execute when the side menu toggle button is clicked. */
  @property({ attribute: false })
  onSideMenuToggle?: () => void

  /** @internal */
  @property({ type: Boolean, attribute: 'is-static' })
  isStatic = false

  /** @internal */
  @provide({ context: navbarContext })
  @state()
  private navbarContext?: NavbarPart

  /** @internal */
  @state()
  private marketingDataLoaded = false

  /** @internal */
  @state()
  private ecoAssist: EcoAssist | undefined

  /** @internal */
  @state()
  private searchExpanded = false

  /** @internal */
  @state()
  private showAppSwitcher?: boolean

  /** @internal */
  @state()
  private showSearchBar = false

  /** @internal */
  @state()
  private mobileMenuOpen = false

  /** @internal */
  @state()
  private marketingData?: NavbarAppSwitcherMarketingData

  /** @internal */
  private get hasSearch() {
    return !!this.searchSlotElements?.length
  }

  /** @internal */
  private get isDefaultLogo() {
    return this.logoSrc === DEFAULT_LOGOMARK_SRC
  }

  /** @internal */
  get resolvedSkipTargets(): ResolvedTarget[] {
    if (this.skipToContent) {
      const items = Array.isArray(this.skipToContent) ? this.skipToContent : [this.skipToContent]
      return items.map((item, idx) => ({
        label: item.label ?? (idx === 0 ? $t('navbar.mainContent') : `${idx + 1}`),
        target: item.target,
      }))
    }

    const firstFocusableEl = <HTMLElement | null>document.querySelector('a, button, input')
    if (firstFocusableEl) {
      return [{ label: $t('navbar.mainContent'), target: firstFocusableEl }]
    }
    return []
  }

  /** @internal */
  private get currentProductName() {
    if (!this.currentProduct) return 'EcoOnline'

    const data = this.marketingData ?? fallbackMarketingData
    if (data) {
      const ns = data.namespaces.find(n => n.products.includes(this.currentProduct!))
      if (ns) return NAMESPACE_LABELS[ns.identifier] ?? 'EcoOnline'
    }
    return NAMESPACE_LABELS[this.currentProduct] ?? 'EcoOnline'
  }

  /**
   * Computed visual state for the App Switcher slot:
   * - `'hidden'` — render nothing (boot not started yet; guest; feature flag resolved false; or no flag
   *   domain could be resolved).
   * - `'pending'` — render the disabled placeholder while marketing data or the
   *   feature-flag check is still in flight.
   * - `'visible'` — render the real `eco-suite-navbar-app-switcher`.
   * @internal
   */
  private get appSwitcherState(): 'hidden' | 'pending' | 'visible' {
    if (!this._booted || this.isGuest || this.showAppSwitcher === false) return 'hidden'
    if (this.marketingDataLoaded && this.showAppSwitcher === true) return 'visible'
    return 'pending'
  }

  constructor() {
    super()
    const contextRoot = new ContextRoot()
    contextRoot.attach(this)
  }

  connectedCallback() {
    super.connectedCallback()
    subscribe(this)

    const debouncedSetDeviceSize = debounce(() => this.setDeviceSize(), 300)
    this.resizeObserver = new ResizeObserver(() => debouncedSetDeviceSize())
    this.resizeObserver.observe(document.body)

    this.setDeviceSize()

    void this.fetchMarketingData()
  }

  protected willUpdate(changedProperties: PropertyValues): void {
    const c = (property: keyof this) => changedProperties.has(property)

    if (c('locale')) void setLocale(this.locale)

    if (
      c('currentProduct')
      || c('assistDomain')
      || c('region')
      || c('organisationId')
      || c('tenantId')
      || c('getToken')
      || c('ecoIdDomain')
      || c('featureFlagDomain')
      || c('isGuest')
    ) {
      this._maybeBoot()
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    setErrorReporter(undefined)
    unsubscribe(this)
    this.resizeObserver?.disconnect()
    this.removeAssistEventListeners()
  }


  /**
   * Called from `willUpdate` whenever a boot-relevant property changes. Fires
   * `_boot()` exactly once — the first time the minimum required set of inputs
   * is present.
   * All config properties should be set in the same synchronous tick
   * (either statically at mount or altogether from a single config response)
   * as post-boot changes will not take effect.
   * @internal
   */
  private _maybeBoot() {
    if (this._booted) return

    if (this.isGuest) {
      this._booted = true
      return
    }

    // Conditions necessary for booting the component:

    // 1. Do we know the active product?
    if (!this.currentProduct) return
    // 2. Can we figure out the Feature Flag Service domain?
    if (!(this.featureFlagDomain || this.region || this.assistDomain)) return
    // 3. Can we figure out the Assist domain?
    if (!(this.assistDomain || this.region)) return
    // 4. Do we have the organisation context (or the necessary information to fetch it)?
    const hasOrgContext = !!(this.organisationId ?? this.tenantId)
    const hasEcoIdConfig = !!(this.getToken && (this.ecoIdDomain || this.region))
    if (!(hasOrgContext || hasEcoIdConfig)) return

    // Boot!
    this._booted = true
    void this._boot()
  }

  /** @internal */
  private async _boot() {
    // Step 1: resolve the EcoID domain and fetch user info if using the self-fetch path.
    if (this.getToken && (this.ecoIdDomain || this.region)) {
      // Priority: explicit prop → region map → undefined.
      const ecoIdDomain =
        (this.ecoIdDomain || undefined)
        ?? ECOID_DOMAINS[this.region as Region ?? '']

      if (ecoIdDomain) {
        await this.fetchUserData(ecoIdDomain)
      } else {
        logError(new Error('No EcoID domain could be resolved. Set a valid `eco-id-domain` or `region`.'), { source: 'EcoSuiteNavbar._boot' })
      }
    }

    if (!(this.organisationId || this.tenantId)) {
      logError(new Error('No organisation or tenant ID could be resolved. Some features will not work as expected'), { source: 'EcoSuiteNavbar._boot' })
      this.showAppSwitcher = false
      return
    }

    // Step 2: resolve the Assist domain.
    // Priority: explicit prop → region map → undefined.
    const assistDomain =
      (this.assistDomain || undefined)
      ?? ASSIST_DOMAINS[this.region as Region ?? '']

    if (!assistDomain) {
      logError(new Error('No Assist domain could be resolved. Set a valid `assist-domain` or `region`.'), { source: 'EcoSuiteNavbar._boot' })
    }

    // Step 3: resolve the Feature Flag domain.
    // We include the assistDomain mapping fallback here for backward-compatibility only, as
    // Feature Flag Service calls were added post-V1.
    // Priority: explicit prop → region map → assistDomain map → undefined (hides App Switcher).
    const featureFlagDomain =
      (this.featureFlagDomain || undefined)
      ?? FEATURE_FLAG_DOMAINS[this.region as Region ?? '']
      ?? mapAssistDomainToFeatureFlagDomain(this.assistDomain)

    if (!featureFlagDomain) {
      logError(new Error('No Feature Flag domain could be resolved. Set a valid `feature-flag-domain` or `region`.'), { source: 'EcoSuiteNavbar._boot' })
    }

    // Step 4: load Assist and call the Feature Flag service in parallel.
    // Both depend on the org/tenant ID which should now be available via
    // either direct properties or the EcoID self-fetch path.
    await Promise.all([
      this.loadAssist(assistDomain),
      this.checkAppSwitcherVisibility(featureFlagDomain),
    ])
  }

  /** @internal */
  private async fetchMarketingData() {
    const marketingDataApi = new MarketingDataApi()
    this.marketingData = await marketingDataApi.fetchMarketingData()
    this.marketingDataLoaded = true
  }

  /** @internal */
  private async fetchUserData(ecoIdDomain: string) {
    if (!(await this.getToken!())) {
      logError(new Error('getToken is not returning a valid EcoID token.'), { source: 'EcoSuiteNavbar.fetchUserData' })
      return
    }

    try {
      const identityApi = new IdentityApi(ecoIdDomain, this.getToken)
      const user = await identityApi.fetchUser()
      if (!user) throw new Error('Issue fetching user data')

      const { userInfo, userData } = user
      this.userName = userInfo?.name
      this.organisationId = userInfo?.['ecoid:organisation']
      this.organisationName = userInfo?.organisation_name
      this.userProducts = userData?.data?.applications ?? []
      const logoSrc = userInfo?.organisation_logo_url
      this.logoSrc = logoSrc?.length ? logoSrc : DEFAULT_LOGOMARK_SRC
      this.platformUrl = userData?.data?.links?.homepage
    } catch (e) {
      logError(e, { source: 'EcoSuiteNavbar.fetchUserData' })
    }
  }

  /** @internal */
  private async loadAssist(assistDomain?: string) {
    if (!assistDomain) return

    const assistApi = new AssistApi(assistDomain, this.organisationId, this.tenantId)
    assistApi.addScriptTag()
    await customElements.whenDefined('eco-assist')
    this.ecoAssist = document.querySelector('eco-assist') ?? undefined

    if (this.ecoAssist) {
      this.ecoAssist.product = this.currentProduct
      this.ecoAssist.organisationId = this.organisationId ?? this.tenantId
      this.addAssistEventListeners()
    }
  }

  /** @internal */
  private async checkAppSwitcherVisibility(flagDomain?: string) {
    if (!flagDomain) {
      this.showAppSwitcher = false
      return
    }

    const featureFlagApi = new FeatureFlagApi(flagDomain, this.organisationId, this.tenantId, this.currentProduct!)
    this.showAppSwitcher = await featureFlagApi.checkAppSwitcherVisibility()
  }

  /** @internal */
  private addAssistEventListeners() {
    if (!this.ecoAssist) return

    this.ecoAssist.addEventListener('open', this._onAssistOpen)
    this.ecoAssist.addEventListener('close', this._onAssistClose)
    this.ecoAssist.addEventListener('minimise', this._onAssistMinimise)
  }

  /** @internal */
  private removeAssistEventListeners() {
    if (!this.ecoAssist) return

    this.ecoAssist.removeEventListener('open', this._onAssistOpen)
    this.ecoAssist.removeEventListener('close', this._onAssistClose)
    this.ecoAssist.removeEventListener('minimise', this._onAssistMinimise)
  }

  /** @internal */
  private setDeviceSize() {
    // Breakpoint values from the prism-design-tokens package. A Rolldown issue prevents us from importing the JSON tokens from a peer dependency with the correct import attributes so we're hardcoding them here for now.
    const smScreen = 480
    const mdScreen = 768
    const navbarContext = globalThis.matchMedia(`(width < ${smScreen}px)`).matches ? 'mobile' : 'desktop'
    const showSearchBar = globalThis.matchMedia(`(width < ${mdScreen}px)`).matches
    if (this.navbarContext === navbarContext && this.showSearchBar === showSearchBar) return

    this.navbarContext = navbarContext
    this.showSearchBar = showSearchBar
  }

  /** @internal */
  private toggleSearch(force?: boolean, autoFocus = false) {
    this.searchExpanded = force ?? !this.searchExpanded
    if (autoFocus) this.searchToggleButton?.focus()

    if (this.searchExpanded) {
      this._clickListener = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const searchSlot = this.shadowRoot?.querySelector('slot[name="search"]') as HTMLSlotElement
        const isSearchSlot = searchSlot?.assignedElements().includes(target)
        if (!isSearchSlot && this.searchExpanded) this.toggleSearch(false)
      }
      document.addEventListener('click', this._clickListener)

      if ('CloseWatcher' in globalThis) {
        this.searchCloseWatcher?.destroy()
        this.searchCloseWatcher = new CloseWatcher()
        this.searchCloseWatcher.onclose = () => this.toggleSearch(false, true)
      }

      if (this.showSearchBar) emit(this, 'eco-search-expanded')
    } else {
      this.searchCloseWatcher?.destroy()
      if (this._clickListener) {
        document.removeEventListener('click', this._clickListener)
        this._clickListener = undefined
      }
      if (this.showSearchBar) emit(this, 'eco-search-collapsed')
    }
  }

  /** @internal */
  private handleSearchKeydown(e: KeyboardEvent) {
    if (this.showSearchBar) {
      if (isArrowUp(e) || isShift(e)) {
        this.toggleSearch(false, true)
      }
      return
    }
    const closing = isTab(e)
    this.toggleSearch(!closing, closing)
  }

  /** @internal */
  private handleSearchKeyup(e: KeyboardEvent) {
    if (!this.showSearchBar && isTab(e)) {
      this.toggleSearch(true)
    }
  }

  /** @internal */
  private async handleSearchIconKeydown(e: KeyboardEvent) {
    if (isArrowDown(e)) {
      if (!this.searchExpanded) {
        this.toggleSearch(true)
        await this.updateComplete
      }
      this.searchSlotElements?.[0]?.focus()
    }
  }

  /** @internal */
  private toggleMobileMenu(e: MouseEvent) {
    e.stopPropagation()
    this.mobileMenu?.toggle?.()
  }

  render() {
    return html`
      <nav
        class=${classMap({
          EcoSuiteNavbar: true,
          'EcoSuiteNavbar--static': !!this.isStatic,
          'EcoSuiteNavbar--searchBarExpanded': this.showSearchBar && this.searchExpanded,
        })}
      >
        ${
          this.resolvedSkipTargets.length ? html`
            <eco-suite-navbar-skip-to-content
              .targets=${this.resolvedSkipTargets}
              @eco-dismissed=${() => this.appSwitcher?.focus()}
            ></eco-suite-navbar-skip-to-content>
          ` : nothing
        }

        <div class="EcoSuiteNavbar__row">
          <div class="EcoSuiteNavbar__left">
            ${
              this.hasSideMenu ? html`
                <eco-suite-navbar-side-menu-button
                  part="side-menu-toggle"
                  .onToggle=${this.onSideMenuToggle}
                  .isActive=${this.sideMenuOpen}
                  data-qa-id="side-menu-button"
                >
              ` : nothing
            }

            ${
              this.appSwitcherState === 'hidden'
                ? nothing
                : this.appSwitcherState === 'visible'
                  ? html`
                    <eco-suite-navbar-app-switcher
                      .userProducts=${this.userProducts}
                      .currentProduct=${this.currentProduct}
                      .homeHref=${this.homeHref}
                      .marketingData=${this.marketingData}
                      .platformUrl=${this.platformUrl}
                    ></eco-suite-navbar-app-switcher>`
                  : html`
                    <div class="EcoSuiteNavbar__appSwitcherSpinner" aria-hidden="true">
                      <pwc-icon-shadow icon="loading" size="sm"></pwc-icon-shadow>
                    </div>`
            }

            <eco-suite-navbar-tooltip content=${$t('navbar.home')} class="EcoSuiteNavbar__homeTooltip">
              <a
                class=${classMap({
                  EcoSuiteNavbar__homeLink: true,
                  'EcoSuiteNavbar__homeLink--defaultLogo': this.isDefaultLogo,
                })}
                href=${this.homeHref}
                data-qa-id="home-link"
                @click=${this.onHomeClick}
              >
                <img
                  src=${this.logoSrc}
                  alt=${this.isDefaultLogo ? 'EcoOnline' : (this.organisationName ?? 'EcoOnline')}
                />
                ${this.marketingDataLoaded ? html`<span>${this.currentProductName}</span>` : nothing}
              </a>
            </eco-suite-navbar-tooltip>
            <slot name="home-suffix"></slot>
          </div>

          <div class="EcoSuiteNavbar__right">
            ${
              this.showSearchBar ? nothing : html`
                <div class=${classMap({
                  EcoSuiteNavbar__search: true,
                  'EcoSuiteNavbar__search--hasSearch': this.hasSearch,
                  'EcoSuiteNavbar__search--expanded': this.searchExpanded,
                })}>
                  <slot
                    name="search"
                    @keydown=${this.handleSearchKeydown}
                    @keyup=${this.handleSearchKeyup}
                    @click=${() => this.toggleSearch(true)}
                  ></slot>
                </div>
              `
            }

            ${
              this.hasSearch ? html`
                <eco-suite-navbar-button
                  class="EcoSuiteNavbar__searchBarToggle"
                  icon="search"
                  .isActive=${this.searchExpanded}
                  .forceNavbarContext=${'desktop'}
                  aria-controls="search-bar"
                  aria-expanded=${this.searchExpanded}
                  tooltip=${$t('navbar.search')}
                  aria-label=${$t('navbar.search')}
                  data-qa-id="search-toggle-button"
                  @click=${(e: MouseEvent) => {
                    e.stopImmediatePropagation()
                    this.toggleSearch()
                  }}
                  @keydown=${this.handleSearchIconKeydown}
                ></eco-suite-navbar-button>
              ` : nothing
            }

            ${
              this.navbarContext === 'mobile' ? nothing : html`<slot class="hideOnMobile" name="utilities"></slot>`
            }

            ${
              this.navbarContext === 'mobile' ? nothing : html`
                <eco-suite-navbar-help
                  .menuItems=${this.helpMenuItems}
                  .href=${this.helpHref}
                  .onHelpClick=${this.onHelpClick}
                  class="hideOnMobile"
                >
                  <slot name="help-menu"></slot>
                </eco-suite-navbar-help>
              `
            }

            ${
              (this.isGuest || this.navbarContext === 'mobile') ? nothing
                : (this.disableAssist || !this.ecoAssist)
                  ? html`
                    <eco-suite-navbar-button
                      label="Assist"
                      icon="eco-ai"
                      class="hideOnMobile"
                      disabled
                    ></eco-suite-navbar-button>`
                  : html`
                    <eco-suite-navbar-tooltip
                      content="Assist"
                      class="hideOnMobile"
                      data-qa-id="assist-button-tooltip"
                    >
                      <eco-assist-trigger-icon
                        .ecoAssist=${this.ecoAssist}
                        data-qa-id="assist-button"
                      ></eco-assist-trigger-icon>
                    </eco-suite-navbar-tooltip>`
            }

            ${
              (this.hideAccountMenu || this.navbarContext === 'mobile') ? nothing : html`
                <eco-suite-navbar-account-menu
                  .isGuest=${this.isGuest}
                  .userName=${this.userName}
                  .menuItems=${this.accountMenuItems}
                  .onLogout=${this.onLogout}
                  .logoutHref=${this.logoutHref}
                  class="hideOnMobile"
                >
                  <slot name="account-menu"></slot>
                </eco-suite-navbar-account-menu>
              `
            }

            ${
              this.navbarContext === 'desktop' ? nothing : html`
                <eco-suite-navbar-button
                  class="EcoSuiteNavbar__mobileToggle"
                  .isActive=${this.mobileMenuOpen}
                  icon="three-dots-vertical"
                  .forceNavbarContext=${'desktop'}
                  aria-controls="mobile-menu"
                  aria-expanded=${this.mobileMenuOpen}
                  tooltip=${$t('navbar.menu')}
                  aria-label=${$t('navbar.menu')}
                  data-qa-id="mobile-menu-button"
                  @click=${this.toggleMobileMenu}
                  @mousedown=${(e: MouseEvent) => {
                    if (this.mobileMenuOpen) e.stopImmediatePropagation()
                  }}
                ></eco-suite-navbar-button>
              `
            }
          </div>
        </div>

        <div
          id="search-bar"
          class=${classMap({
            'EcoSuiteNavbar__searchBar': true,
            'EcoSuiteNavbar__searchBar--expanded': this.searchExpanded,
          })}
          ?inert=${!this.searchExpanded}
        >
          ${this.showSearchBar ? html`
            <slot
              name="search"
              @keydown=${this.handleSearchKeydown}
              @keyup=${this.handleSearchKeyup}
              @click=${() => this.toggleSearch(true)}
            ></slot>
          ` : nothing}
          <pwc-icon-button
            icon="cross"
            class="EcoSuiteNavbar__searchBarClose"
            appearance="neutral"
            variant="transparent"
            size="sm"
            aria-label=${$t('navbar.closeSearch')}
            @click=${() => this.toggleSearch(false, true)}
          ></pwc-icon-button>
        </div>

        ${
          this.navbarContext === 'desktop' ? nothing : html`
            <eco-suite-navbar-mobile-menu
              id="mobile-menu"
              .userName=${this.userName}
              .ecoAssist=${this.ecoAssist}
              .disableAssist=${this.disableAssist}
              .accountMenuItems=${this.accountMenuItems}
              .onLogout=${this.onLogout}
              .logoutHref=${this.logoutHref}
              .helpMenuItems=${this.helpMenuItems}
              .helpHref=${this.helpHref}
              .onHelpClick=${this.onHelpClick}
              .isGuest=${this.isGuest}
              .hideAccountMenu=${this.hideAccountMenu}
              ?inert=${!this.mobileMenuOpen}
              @eco-mobile-menu-opened=${() => {
                this.mobileMenuOpen = true
                this.searchExpanded = false
              }}
              @eco-mobile-menu-closed=${() => {
                this.mobileMenuOpen = false
                this.mobileMenuToggleButton?.focus()
              }}
            >
              <slot slot="account-menu" name="account-menu"></slot>
              <slot slot="utilities" name="utilities"></slot>
              <slot slot="help-menu" name="help-menu"></slot>
            </eco-suite-navbar-mobile-menu>
          `
        }
      </nav>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar': EcoSuiteNavbar
  }
}
